import type { SiteRagReport } from '../../types/siteRag';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
/** Read only content-addressed saved bytes. A hydrated reader body is metadata
 * for this purpose; it can never supply historical summary facts on its own. */
export async function loadFrozenSummaryReport(metadata: SiteRagReport, loadJson: (path: string) => Promise<unknown>): Promise<SiteRagReport> {
  const revision = metadata.reportRevision;
  if (!revision) throw new Error('This report has no immutable screening revision.');
  const modern = Boolean(revision.sourcePath && revision.sourceHash);
  const path = modern ? revision.sourcePath! : (metadata as SiteRagReport & { storagePath?: string }).storagePath;
  const bodySha = path?.match(/\/body\/([a-f0-9]{64})\.json$/)?.[1];
  const legacyImmutable = Boolean(bodySha && path?.endsWith(`/reports/${metadata.id}/revisions/${revision.revisionId}/body/${bodySha}.json`));
  if (!path || (!modern && !legacyImmutable)) throw new Error('The frozen bytes for this historical screening revision are unavailable.');
  const value = await loadJson(path);
  if (!value || typeof value !== 'object' || Array.isArray(value) || hashCanonicalJson(value) !== (modern ? revision.sourceHash : bodySha)) throw new Error('The saved screening revision failed its content check.');
  const frozen = value as SiteRagReport;
  if (frozen.id !== metadata.id || frozen.reportType !== 'feasibility'
    || (metadata.siteId && frozen.siteId !== metadata.siteId)
    || (!modern && (!frozen.reportRevision || frozen.reportRevision.revisionId !== revision.revisionId
      || frozen.reportRevision.documentHash !== revision.documentHash || frozen.reportRevision.presentationHash !== revision.presentationHash
      || frozen.reportRevision.documentPath !== revision.documentPath || frozen.reportRevision.presentationPath !== revision.presentationPath
      || frozen.reportRevision.sourcePath !== revision.sourcePath || frozen.reportRevision.sourceHash !== revision.sourceHash
      || hashCanonicalJson(frozen.reportRevision.deliveryDecision) !== hashCanonicalJson(revision.deliveryDecision)
      || hashCanonicalJson(frozen.reportRevision.artifacts.pdf ?? null) !== hashCanonicalJson(revision.artifacts.pdf ?? null)))) throw new Error('The saved screening body belongs to a different revision.');
  const metaOwner = metadata as SiteRagReport & {
    reportOwnerUid?: string;
    siteOwnerUid?: string;
  };
  const pathOwnerUid = /^users\/([^/]+)\/reports\//.exec(path)?.[1];
  const resolvedOwnerUid =
    (typeof metadata._ownerUid === 'string' && metadata._ownerUid.trim() ? metadata._ownerUid.trim() : undefined) ??
    (typeof metaOwner.reportOwnerUid === 'string' && metaOwner.reportOwnerUid.trim() ? metaOwner.reportOwnerUid.trim() : undefined) ??
    (pathOwnerUid && pathOwnerUid.trim() ? pathOwnerUid.trim() : undefined);
  return {
    ...frozen,
    reportRevision: revision,
    pdfDelivery: metadata.pdfDelivery,
    ...(metadata.analysisRevision !== undefined ? { analysisRevision: metadata.analysisRevision } : {}),
    ...(metadata.analysisRevisionId !== undefined ? { analysisRevisionId: metadata.analysisRevisionId } : {}),
    ...(metadata.analysisInputHash !== undefined ? { analysisInputHash: metadata.analysisInputHash } : {}),
    ...(metadata.analysisContentHash !== undefined ? { analysisContentHash: metadata.analysisContentHash } : {}),
    ...(metadata.analysisReadiness !== undefined ? { analysisReadiness: metadata.analysisReadiness } : {}),
    ...(metadata.analysisState !== undefined ? { analysisState: metadata.analysisState } : {}),
    ...(metadata.analysisRevisions !== undefined ? { analysisRevisions: metadata.analysisRevisions } : {}),
    ...(resolvedOwnerUid ? { _ownerUid: resolvedOwnerUid } : {}),
    ...(typeof metaOwner.reportOwnerUid === 'string' && metaOwner.reportOwnerUid.trim()
      ? { reportOwnerUid: metaOwner.reportOwnerUid }
      : resolvedOwnerUid
        ? { reportOwnerUid: resolvedOwnerUid }
        : {}),
    ...(typeof metaOwner.siteOwnerUid === 'string' && metaOwner.siteOwnerUid.trim()
      ? { siteOwnerUid: metaOwner.siteOwnerUid }
      : {}),
  };
}
